import { Toast } from '../../../components/Common/Toast';
import { CONFIG } from '../../../config/mainConfig';
import { BookModel } from '../../../interfaces/book.interface';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { UserModel } from '../../../interfaces/user.interface';
import { callAPI } from '../../../services/api.service';
import { AppDispatch } from '../../../store/store';
import { setStoryData } from '../../../store/storySlice';

interface CallImageGeneratorParams {
  pageKey: string;
  storyPrompt: string;
  storyText: string;
  bookId: string;
  pageData?: any;
  bookData?: any;
  storyData: any;
  setLoader: (loading: boolean) => void;
  dispatch: any;
  setShowEditTextIllustration: (show: boolean) => void;
  setShowLowCreditModal: (show: boolean) => void;
  closeComponent: () => void;
}

interface UpdateTextParams {
  bookId: string;
  pageIndex: number;
  updatedText: string;
  updatedScene: string;
  setLoader: (state: boolean) => void;
  displayBookPages: () => void;
  setShowEditTextIllustration: (state: boolean) => void;
  dispatch: AppDispatch;
  storyData: BookModel;
  closeComponent: () => void;
  ignoreImageGeneration?: boolean;
}

export const callImageGenerator = async ({
  pageKey,
  storyPrompt,
  storyText,
  bookId,
  pageData,
  bookData,
  storyData,
  setLoader,
  dispatch,
  setShowEditTextIllustration,
  setShowLowCreditModal,
  closeComponent,
}: CallImageGeneratorParams) => {
  try {
    setLoader(true);
    const response = await callAPI('api/generate-single-image', {
      bookId,
      pageKey,
      storyPrompt,
      imagePromptCharacterUrl: pageData?.imagePromptCharacterUrl
        ? pageData?.imagePromptCharacterUrl
        : bookData?.characterPortrait,
    });

    if (response?.status === HTTPStatusEnum.OK) {
      Toast('New Image Will Display Soon');
      setShowEditTextIllustration(false);
      dispatch(setStoryData({ illustrationCharacter: null }));
      closeComponent();
    } else if (response?.error) {
      // Don't surface raw server error strings to the user — they're not
      // copy-controlled and may contain technical detail. Log and show a
      // safe generic message; the per-page error UI (banner + tile copy)
      // will still drive the user to Edit scene with full context.
      console.error('[generate-single-image] server error:', response.error);
      Toast('Something went wrong. Please try again.', true);
      dispatch(setStoryData({ lastHandledErrorAt: Date.now() }));
    } else if (response?.status === HTTPStatusEnum.BAD_REQUEST) {
      dispatch(
        setStoryData({
          creditsPerBookLimit: parseInt(storyData?.creditsPerBookLimit) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
        }),
      );
      Toast(response?.message, true);
      setShowLowCreditModal(true);
      closeComponent();
    } else {
      Toast('Something went wrong. Please try again.', true);
    }
  } catch (error) {
    Toast('An unexpected error occurred!', true);
  } finally {
    setLoader(false);
  }
};

export const updateText = async ({
  bookId,
  pageIndex,
  updatedText,
  updatedScene,
  setLoader,
  displayBookPages,
  setShowEditTextIllustration,
  dispatch,
  storyData,
  closeComponent,
  ignoreImageGeneration,
}: UpdateTextParams) => {
  try {
    setLoader(true);
    await callAPI('api/update-book-text', {
      bookId,
      pageIndex,
      updatedText,
      updatedScene,
    });
    if (!ignoreImageGeneration) {
      displayBookPages();
    }
  } catch (error) {
  } finally {
    setLoader(false);
    setShowEditTextIllustration(false);
    dispatch(
      setStoryData({
        ...storyData,
        illustrationCharacter: null,
      }),
    );
    closeComponent();
  }
};

export const updateUser = async (user: UserModel | null, screen: string) => {
  try {
    await callAPI('user/updateUser', {
      email: user?.email,
      firstName: user?.firstName,
      lastName: user?.lastName,
      settings: user?.settings,
      screentoUpdate: screen,
    });
  } catch (error) {}
};

export const deleteCharacter = async (
  email: string,
  characterUrl: string | undefined,
  getCharacterLibrary: (
    email: string,
    loaders: any,
    setLoaders: (loaders: any) => void,
    setLibrary: (library: any) => void,
  ) => void,
  loaders: any,
  setLoaders: (loaders: any) => void,
  setLibrary: (library: any) => void,
  setSelectedPortrait: (portrait: any) => void,
  setOpenDialog: (isOpen: boolean) => void,
) => {
  try {
    const response = await callAPI(`api/book-update/remove-character`, {
      email,
      characterUrl,
    });

    if (response?.status === HTTPStatusEnum.OK) {
      getCharacterLibrary(email, loaders, setLoaders, setLibrary);
      setSelectedPortrait(null);
      setOpenDialog(false);
      Toast('Character Deleted Successfully');
    } else {
      Toast('Character Not Deleted', true);
    }
  } catch (error) {
    Toast('An error occurred while deleting the character', true);
  }
};
